/* eslint-disable camelcase */
import * as d3 from "d3";
import { RawData, SeriesGraph } from "./series_graph";
import { i18n } from "i18n/i18n";

interface CumulativeTimeseriesData {
    ex_id: string;
    ex_data: { x1: number; cSum: number }[];
}

/**
 * Displays a cumulative timeseries graph, showing the cumulative progress
 * of submissions over time for different exercises.
 * Extends the SeriesGraph to include functionality specific to cumulative data.
 */
export class CTimeseriesGraph extends SeriesGraph {
    protected readonly baseUrl = "/stats/cumulative_timeseries?series_id=";
    protected readonly margin = { top: 20, right: 50, bottom: 80, left: 50 };

    // Bisector used for finding the closest bin index.
    private readonly bisector = d3.bisector((d: Date | number) => d).left;

    // Scales
    private x: d3.ScaleTime<number, number>;
    private y: d3.ScaleLinear<number, number>;
    private color: d3.ScaleOrdinal<string, unknown>;

    // Axes
    private xAxis: d3.Selection<SVGGElement, unknown, HTMLElement, unknown>;
    private yAxis: d3.Selection<SVGGElement, unknown, HTMLElement, unknown>;

    // Tooltip elements
    private tooltip: d3.Selection<HTMLDivElement, unknown, HTMLElement, unknown>;
    private tooltipIndex = -1; // Prevent unnecessary tooltip updates.
    private tooltipLine: d3.Selection<SVGLineElement, unknown, HTMLElement, unknown>;
    private tooltipDots;

    // Data and configuration
    private data: CumulativeTimeseriesData[] = [];
    private studentCount: number; // Total number of students/subscribers.
    private maxSum = 0; // Maximum cumulative sum across bins.
    private minDate: Date;
    private maxDate: Date;
    private binTicks: number[];
    private binStep: number;
    private y100 = false; // If true, y-axis domain is [0,1] (i.e. 100%); otherwise based on data.

    /**
     * Draws the cumulative timeseries graph including axes, lines, tooltips, and legend.
     *
     * @param {boolean} animate - Whether to play animations (default: true).
     */
    protected override draw(animate = true): void {
        if (this.binTicks.length < 2) {
            this.drawNoData();
            return;
        }

        // Set a fixed height for this graph.
        this.height = 400;
        super.draw();

        // Define scales.
        this.y = d3
            .scaleLinear()
            .domain([0, this.y100 ? 1 : this.maxSum / this.studentCount])
            .range([this.innerHeight, 0]);
        this.x = d3.scaleTime().domain(this.binTicks).range([0, this.innerWidth]);
        this.color = d3.scaleOrdinal().range(this.observable10).domain(this.exerciseOrder);

        // Append axis groups.
        this.yAxis = this.graph.append("g");
        this.xAxis = this.graph.append("g");

        // Initialize tooltip elements.
        this.tooltip = this.container
            .append("div")
            .attr("class", "d3-tooltip")
            .attr("pointer-events", "none")
            .style("opacity", 0)
            .style("z-index", 5);
        this.tooltipLine = this.graph
            .append("line")
            .attr("y1", 0)
            .attr("y2", this.innerHeight)
            .attr("opacity", 0)
            .attr("pointer-events", "none")
            .attr("stroke", "currentColor");
        this.tooltipDots = this.graph
            .selectAll(".tooltipDot")
            .data(this.data, (ex: CumulativeTimeseriesData) => ex.ex_id)
            .join("circle")
            .attr("class", "tooltipDot")
            .attr("opacity", 0)
            .attr("r", 4)
            .style("fill", (ex) => this.color(ex.ex_id) as string);

        // Initialize the legend.
        this.legendInit();

        // Add baseline lines for each exercise.
        this.graph
            .selectAll(".line")
            .data(this.data, (ex: CumulativeTimeseriesData) => ex.ex_id)
            .join("path")
            .attr("class", "line")
            .style("stroke", (ex) => this.color(ex.ex_id) as string)
            .style("fill", "none")
            // Initially draw lines at the bottom (will be updated in drawUpdate).
            .attr("d", (ex) =>
                d3
                    .line<{ x1: number; cSum: number }>()
                    .x((d) => this.x(d.x1))
                    .y(this.innerHeight)
                    .curve(d3.curveMonotoneX)(ex.ex_data),
            );

        // Attach tooltip events.
        this.svg.on("mouseover", (e) => this.tooltipOver(e));
        this.svg.on("mousemove", (e) => this.tooltipMove(e));
        this.svg.on("mouseleave", () => this.tooltipOut());

        // Make y-axis area clickable to toggle range.
        this.graph
            .append("rect")
            .attr("width", 50)
            .attr("height", this.innerHeight + 10)
            .attr("x", -50)
            .attr("y", -10)
            .attr("fill", "none")
            .attr("pointer-events", "all")
            .style("cursor", "pointer")
            .on("click", () => this.yRangeToggle());

        this.drawUpdate(animate);
    }

    /**
     * Updates the graph elements with transitions.
     *
     * @param {boolean} animation - Whether to animate the update (default: true).
     */
    private drawUpdate(animation = true): void {
        // Update y-scale and axis.
        this.y = d3
            .scaleLinear()
            .domain([0, this.y100 ? 1 : this.maxSum / this.studentCount])
            .range([this.innerHeight, 0]);
        this.yAxis
            .transition()
            .duration(animation ? 500 : 0)
            .call(d3.axisLeft(this.y).ticks(5, "%"));

        // Update x-scale and axis.
        this.x = d3.scaleTime().domain([this.minDate, this.maxDate]).range([0, this.innerWidth]);
        this.xAxis
            .attr("transform", `translate(0, ${this.innerHeight})`)
            .call(
                d3
                    .axisBottom(this.x)
                    .tickValues(this.binTicks)
                    .tickFormat((t: Date) => {
                        const asDate = new Date(t);
                        const timeZoneDiff = (asDate.getTimezoneOffset() - this.minDate.getTimezoneOffset()) / 60;
                        return this.binStep >= 24 ||
                            (asDate.getHours() === (24 - timeZoneDiff) % 24 && asDate.getMinutes() === 0)
                            ? d3.timeFormat(i18n.t("date.formats.weekday_short"))(t)
                            : d3.timeFormat(i18n.t("time.formats.plain_time"))(t);
                    }),
            )
            .selectAll("text")
            .style("text-anchor", "end")
            .attr("dy", ".7em")
            .attr("transform", "rotate(-25)");

        // Transition the lines to their new positions.
        this.graph
            .selectAll(".line")
            .data(this.data, (ex: CumulativeTimeseriesData) => ex.ex_id)
            .join("path")
            .transition()
            .duration(animation ? 500 : 0)
            .attr("d", (ex) =>
                d3
                    .line<{ x1: number; cSum: number }>()
                    .x((d) => this.x(d.x1))
                    .y((d) => this.y(d.cSum / this.studentCount))
                    .curve(d3.curveMonotoneX)(ex.ex_data),
            );
    }

    /**
     * Transforms the raw data into cumulative timeseries data.
     *
     * @param {RawData<string | Date>} raw - The unprocessed data from the server.
     */
    protected override processData({ data, exercises, student_count, deadline }: RawData<string | Date>): void {
        this.data = [];

        // Map and filter exercises.
        this.parseExercises(
            exercises,
            data.map((ex) => ex.ex_id),
        );

        // Convert each date string into a Date object.
        data.forEach((exercise) => {
            exercise.ex_data = exercise.ex_data.map((d: string) => new Date(d));
        });

        // Determine the overall minimum and maximum dates.
        let [minDate, maxDate] = d3.extent(data.flatMap((exercise) => exercise.ex_data as Date[])) as Date[];

        if (deadline) {
            maxDate = deadline;
        }

        this.minDate = this.dateStart ? new Date(this.dateStart) : new Date(minDate);
        this.maxDate = this.dateEnd ? new Date(this.dateEnd) : new Date(maxDate);

        // Aim for roughly 17 bins (between 15 and 20 bins).
        const [binStep, binTicks, alignedStartTime] = this.findBinTime(this.minDate, this.maxDate, 17);
        this.binStep = binStep;
        this.binTicks = binTicks as number[];
        this.minDate = alignedStartTime;
        this.maxDate = new Date(this.binTicks[this.binTicks.length - 1]);

        if (this.binTicks.length < 2) return;

        if (!this.dateStart) {
            this.setPickerDates(this.minDate, this.maxDate);
        }

        this.studentCount = student_count;
        this.maxSum = 0;
        // Bin the data per exercise and calculate cumulative sums.
        data.forEach((exercise) => {
            const binned = d3
                .bin<Date, number>()
                .value((d) => d.getTime())
                .thresholds(this.binTicks as number[])
                .domain([0, this.maxDate.getTime()])(exercise.ex_data as Date[]);
            // Remove the last bin if it has zero width.
            const binAmount = binned.length;
            if (binned[binAmount - 1].x0 === binned[binAmount - 1].x1) {
                binned.pop();
            }
            const cSums = d3.cumsum(binned, (d) => d.length);
            this.data.push({
                ex_id: String(exercise.ex_id),
                ex_data: binned.map((bin, i) => ({ x1: bin["x1"], cSum: cSums[i] })),
            });

            // Update the maximum cumulative sum.
            this.maxSum = Math.max(cSums[cSums.length - 1], this.maxSum);
        });
    }

    /**
     * Finds the nearest bin to the given mouse x-coordinate.
     *
     * @param {number} mouseX - The x-coordinate of the mouse pointer.
     * @returns {{ date: Date; i: number }} The closest date and its index.
     */
    private bisect(mouseX: number): { date: Date; i: number } {
        const minTime = this.minDate.getTime();
        const maxTime = this.maxDate.getTime();

        // Handle edge case when no bin ticks are available.
        if (!this.binTicks) {
            return { date: new Date(0), i: 0 };
        }

        // Convert the mouse x-coordinate to a date.
        const mouseDate = this.x.invert(mouseX);

        // Find the insertion index for the mouseDate.
        const binIndex = this.bisector(this.binTicks, mouseDate, 1);

        const previousDate = binIndex > 0 ? this.binTicks[binIndex - 1] : minTime;
        const nextDate = binIndex < this.binTicks.length ? this.binTicks[binIndex] : maxTime;

        // Return the closest date and corresponding index.
        if (binIndex < this.binTicks.length && mouseDate.getTime() - previousDate > nextDate - mouseDate.getTime()) {
            return { date: new Date(nextDate), i: binIndex };
        } else {
            return { date: new Date(previousDate), i: binIndex - 1 };
        }
    }

    /**
     * Hides all tooltip elements.
     */
    private tooltipOut(): void {
        this.tooltipIndex = -1;
        this.tooltip.style("opacity", 0);
        this.tooltipLine.attr("opacity", 0);
        this.tooltipDots.attr("opacity", 0);
    }

    /**
     * Displays the tooltip for the data point closest to the mouse pointer.
     *
     * @param {unknown} e - The mouse event.
     */
    private tooltipOver(e: unknown): void {
        if (!this.binTicks) return;

        // Find the closest bin based on the mouse x-coordinate.
        const { date: time, i } = this.bisect(d3.pointer(e, this.graph.node())[0]);
        this.tooltip
            .html(this.tooltipText(i))
            .style("left", `${this.x(time) + this.margin.left + 5}px`)
            .style("top", `${this.margin.top}px`);

        this.tooltipLine.attr("x1", this.x(time)).attr("x2", this.x(time));

        this.tooltipDots
            .attr("cx", this.x(time))
            .attr("cy", (exercise) => this.y(exercise.ex_data[i].cSum / this.studentCount));
    }

    /**
     * Updates the tooltip position and content as the mouse moves.
     *
     * @param {unknown} e - The mouse event.
     */
    private tooltipMove(e: unknown): void {
        if (!this.binTicks) return;

        const { date, i } = this.bisect(d3.pointer(e, this.graph.node())[0]);
        if (i === this.tooltipIndex) return;
        this.tooltipIndex = i;

        this.tooltip
            .html(this.tooltipText(i))
            .transition()
            .duration(100)
            .style("opacity", 0.9)
            .style("left", `${this.x(date) + this.margin.left + 5}px`)
            .style("top", `${this.margin.top}px`);

        this.tooltipLine
            .transition()
            .duration(100)
            .attr("opacity", 1)
            .attr("x1", this.x(date))
            .attr("x2", this.x(date));

        this.tooltipDots
            .transition()
            .duration(100)
            .attr("opacity", 1)
            .attr("cx", this.x(date))
            .attr("cy", (exercise) => this.y(exercise.ex_data[i].cSum / this.studentCount));
    }

    /**
     * Constructs the HTML content for the tooltip based on the provided bin index.
     *
     * @param {number} i - The bin index.
     * @returns {string} The HTML content for the tooltip.
     */
    private tooltipText(i: number): string {
        const date = this.binTicks[i];
        let message = "";

        if (this.binStep < 24) {
            const on = i18n.t("js.date_on");
            const timeFormat = d3.timeFormat(i18n.t("time.formats.plain_time"));
            message += timeFormat(new Date(date));
            message += ` ${on} `;
        }

        const weekDay = d3.timeFormat(i18n.t("date.formats.weekday_long"));
        message += weekDay(new Date(date));

        this.exerciseOrder.forEach((e) => {
            const ex = this.data.find((ex) => ex.ex_id === e);
            message += `<br><span style="color: ${this.color(e)}">&FilledSmallSquare;</span> ${d3.format(".1%")(
                ex.ex_data[i].cSum / this.studentCount,
            )} (${ex.ex_data[i].cSum}/${this.studentCount})`;
        });

        return `<b>${message}</b>`;
    }

    /**
     * Toggles the y-axis range between 100% and the maximum data value.
     */
    private yRangeToggle(): void {
        this.y100 = !this.y100;
        this.tooltipOut();
        this.drawUpdate();
    }

    /**
     * Initializes the legend with interactive hover behavior.
     */
    private legendInit(): void {
        const legend = this.container
            .append("div")
            .attr("class", "legend")
            .style("margin-top", "-30px")
            .selectAll("div")
            .data(this.exerciseOrder)
            .enter()
            .append("div")
            .attr("class", "legend-item")
            .on("mouseover", (_, legendId) => {
                this.graph
                    .selectAll(".line")
                    .style("opacity", (lineEx: CumulativeTimeseriesData) => (legendId !== lineEx.ex_id ? 0.2 : 1));
            })
            .on("mouseout", () => {
                this.graph.selectAll(".line").style("opacity", 1);
            });

        legend
            .append("div")
            .attr("class", "legend-box")
            .style("background", (exId) => this.color(exId) as string)
            .style("pointer-events", "none");
        legend
            .append("span")
            .attr("class", "legend-text")
            .text((exId) => this.exerciseMap[exId])
            .style("color", "currentColor")
            .style("font-size", `${this.fontSize}px`)
            .style("pointer-events", "none");
    }

    /**
     * Initializes the cumulative timeseries graph by setting up time pickers and delegating to the parent init.
     *
     * @param {boolean} [draw=true] - Whether to draw the graph after initialization.
     * @param {any} [data] - Optional pre-fetched data.
     * @returns {Promise<void>} A promise that resolves when initialization is complete.
     */
    public override init(draw = true, data = undefined): Promise<void> {
        this.initTimePickers();
        return super.init(draw, data);
    }
}
